import ReCAPTCHA from "react-google-recaptcha"
import config from "@/config"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { Input } from "@/components/_Shared/Form/Inputs/Input"
import { Checkbox } from "@/components/_Shared/Form/Inputs/Checkbox"
import { Submit } from "@/components/_Shared/Form/Buttons/Submit"
import { Successful } from "@/components/_Shared/Form/Buttons/Successful"
import { SendContactUsForm } from "@/api/contactUs"
import { ContactFormState } from "@/interfaces/contactUs_type"

const contactUsSchema = z.object({
    contact_us_email: z.string().email({ message: "Invalid email address" }),
    info: z.object({
      services: z.boolean(),
      pricing: z.boolean(),
      general: z.boolean(),
    }).refine((data) => data.services || data.pricing || data.general, {
      message: "At least one option must be selected"
    }),
    recaptcha: z.string().min(1, { message: "Recaptcha is required" })
})

type ContactUsFormData = z.infer<typeof contactUsSchema>

/**
 * Component where the user can contact Trinity for more information
 */
export function ContactForm() {
    const { 
      register, 
      handleSubmit, 
      setValue,
      setError,
      formState: { errors, isSubmitting, isSubmitSuccessful },
    } = useForm<ContactUsFormData>({
      resolver: zodResolver(contactUsSchema)
    })
    
    const onSubmit = async (data: ContactUsFormData) => {
      const contactUsData: ContactFormState & {recaptcha: string} = {
        email: data.contact_us_email,
        services: data.info.services,
        pricing: data.info.pricing,
        general: data.info.general,
        recaptcha: data.recaptcha
      }

      const response = await SendContactUsForm(contactUsData)
      
      if (response.operation === "ERROR") {
        setError("root", { message: response.message })
      }
    }
  
    return (
      <div className='m-5'>
          <h2 className='px-3 text-xl md:text-2xl'>Want to know more about us?</h2>
  
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col p-3'>

            <Input type="email" label="E-mail" name="contact_us_email" register={register} required />

            {errors.contact_us_email && <p className='text-red-500'>{errors.contact_us_email.message}</p>}

            <label htmlFor='options' className='mt-3 mb-3'>Select all the information you want to know more about:</label>
            <div className="flex flex-col gap-2">

              <Checkbox label="Our Services" name="info.services" register={register} />
              <Checkbox label="Pricing Information" name="info.pricing" register={register} />
              <Checkbox label="General Information" name="info.general" register={register} />

              {errors.info && <p className='text-red-500'>{errors.info.message}</p>}
  
            </div>
            
              <ReCAPTCHA
                sitekey={config.recaptcha.sitekey}
                onChange={(value) => {
                  setValue("recaptcha", value ?? "")
                }}
              />

              {errors.recaptcha && <p className='text-red-500'>{errors.recaptcha.message}</p>}
  

            {isSubmitSuccessful ? <Successful message="We will get back to you as soon as possible." /> 
            : 
              <Submit isSubmitting={isSubmitting} label="Send Email" test_id="contact_us" />
            }

            {errors.root && <p className='text-red-500'>{errors.root.message}</p>}
          </form>
      </div>
    )
  }